import { ReactNode } from 'react';

interface SectionProps {
  children: ReactNode;
  className?: string;
}

export function Section({ children, className = '' }: SectionProps) {
  return (
    <section className={`mb-8 ${className}`}>
      {children}
    </section>
  );
}

interface SectionTitleProps {
  children: ReactNode;
  id?: string;
}

export function SectionTitle({ children, id }: SectionTitleProps) {
  return (
    <h2 id={id} className="text-2xl font-bold text-cyan-400 mb-4 pb-2 border-b border-slate-700">
      {children}
    </h2>
  );
}

interface CardProps {
  children: ReactNode;
  className?: string;
}

export function Card({ children, className = '' }: CardProps) {
  return (
    <div className={`bg-slate-800/50 rounded-lg p-6 border border-slate-700 hover:border-cyan-500/50 transition-all ${className}`}>
      {children}
    </div>
  );
}

interface BadgeProps {
  children: ReactNode;
  variant?: 'default' | 'success' | 'warning' | 'danger';
}

export function Badge({ children, variant = 'default' }: BadgeProps) {
  const variants = {
    default: 'bg-slate-700 text-slate-300',
    success: 'bg-green-500/20 text-green-400',
    warning: 'bg-yellow-500/20 text-yellow-400',
    danger: 'bg-red-500/20 text-red-400',
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${variants[variant]}`}>
      {children}
    </span>
  );
}

interface CaseCardProps {
  icon: string;
  title: string;
  description: string;
  tags: string[];
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  link: string;
}

export function CaseCard({ icon, title, description, tags, difficulty, link }: CaseCardProps) {
  const difficultyConfig = {
    beginner: { label: '初级', color: 'bg-green-100 text-green-800' },
    intermediate: { label: '中级', color: 'bg-yellow-100 text-yellow-800' },
    advanced: { label: '高级', color: 'bg-red-100 text-red-800' },
  };

  const config = difficultyConfig[difficulty];

  return (
    <Card className="h-full flex flex-col">
      <div className="flex items-start justify-between mb-4">
        <span className="text-4xl" aria-hidden="true">{icon}</span>
        <Badge variant={difficulty === 'beginner' ? 'success' : difficulty === 'intermediate' ? 'warning' : 'danger'}>
          {config.label}
        </Badge>
      </div>
      <h3 className="text-xl font-semibold text-white mb-2">{title}</h3>
      <p className="text-slate-400 text-sm mb-4 flex-1">{description}</p>
      <div className="flex flex-wrap gap-2 mb-4">
        {tags.map((tag, index) => (
          <Badge key={index}>{tag}</Badge>
        ))}
      </div>
      <a
        href={link}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center justify-center px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg transition-colors whitespace-nowrap cursor-pointer w-full"
      >
        查看教程
      </a>
    </Card>
  );
}

interface QuickLinkCardProps {
  icon: string;
  title: string;
  description: string;
  link: string;
  bgColor: string;
}

export function QuickLinkCard({ icon, title, description, link, bgColor }: QuickLinkCardProps) {
  return (
    <a
      href={link}
      target="_blank"
      rel="noopener noreferrer"
      className={`${bgColor} rounded-lg p-6 hover:shadow-lg transition-all cursor-pointer block`}
    >
      <div className="text-center">
        <div className="text-3xl mb-2">{icon}</div>
        <div className="font-semibold text-slate-900 mb-1">{title}</div>
        <div className="text-xs text-slate-600">{description}</div>
      </div>
    </a>
  );
}

interface IMCardProps {
  icon: string;
  title: string;
  link: string;
}

export function IMCard({ icon, title, link }: IMCardProps) {
  return (
    <a
      href={link}
      target="_blank"
      rel="noopener noreferrer"
      className="bg-slate-800/50 rounded-lg p-6 border border-slate-700 hover:border-cyan-500/50 hover:shadow-lg transition-all cursor-pointer block text-center"
    >
      <div className="text-3xl mb-3">{icon}</div>
      <div className="font-medium text-white text-sm">{title}</div>
    </a>
  );
}

interface InfoBoxProps {
  children: ReactNode;
  type?: 'info' | 'warning' | 'success';
}

export function InfoBox({ children, type = 'info' }: InfoBoxProps) {
  const styles = {
    info: 'bg-cyan-500/10 border-cyan-500/50 text-cyan-300',
    warning: 'bg-yellow-500/10 border-yellow-500/50 text-yellow-300',
    success: 'bg-green-500/10 border-green-500/50 text-green-300',
  };

  return (
    <div className={`rounded-lg p-6 border ${styles[type]}`}>
      {children}
    </div>
  );
}

interface StepCardProps {
  number: string;
  title: string;
  description: string;
  bgColor: string;
}

export function StepCard({ number, title, description, bgColor }: StepCardProps) {
  return (
    <div className={`${bgColor} rounded-lg p-6`}>
      <div className="text-2xl font-bold mb-2" style={{ color: bgColor.includes('blue') ? '#2563eb' : bgColor.includes('green') ? '#16a34a' : bgColor.includes('purple') ? '#9333ea' : '#ea580c' }}>
        {number}
      </div>
      <h3 className="font-semibold text-slate-900 mb-1">{title}</h3>
      <p className="text-sm text-slate-600">{description}</p>
    </div>
  );
}